import { useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import { signUpDataAtom, pushStepAtom } from "store/signUpStore";
import { IdentityNameTelecomPhoneForm, IdentityNameTelecomPhoneResult } from "components/identity/IdentityNameTelecomPhoneForm";

/**
 * Step3: 이름 + 통신사 + 전화번호 (KR 다날 인증정보)
 *
 *   - 14세 미만 (parentConsent) → 보호자 인증정보 (parentName/parentTelecom/parentCtn)
 *   - 14세 이상              → 본인 인증정보 (name/telecom/ctn)
 *
 * 다음 → Step5 (다날 인증)
 */
export const SignUpStep3 = () => {
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);

    const isParent = signUpData.parentConsent;

    // 입력값 atom 반영 — onValuesChange(실시간) / onNext(최종) 공용.
    //   실시간 저장 덕분에 sideStep 도중 헤더 뒤로가기로 이탈해도 재진입 시 복원됨.
    const persist = useCallback((v: IdentityNameTelecomPhoneResult) => {
        if (isParent) {
            // 보호자 인증정보 → Step5 (DANAL_PARENT)
            setSignUpData(prev => ({
                ...prev,
                parentName: v.name,
                parentTelecom: v.telecom,
                parentCtn: v.phone,
                parentNationality: v.nationality,
            }));
        } else {
            // 14세 이상 본인 → Step5 (DANAL_SELF)
            setSignUpData(prev => ({
                ...prev,
                name: v.name,
                telecom: v.telecom,
                ctn: v.phone,
                ctnCountryCd: "+82",
                ctnCountry: "KR",
                nationality: v.nationality,
            }));
        }
    }, [isParent, setSignUpData]);

    const handleNext = (result: IdentityNameTelecomPhoneResult) => {
        persist(result);
        pushStep(5);
    };

    if (isParent) {
        return (
            <IdentityNameTelecomPhoneForm
                initialName={signUpData.parentName}
                initialTelecom={signUpData.parentTelecom}
                initialPhone={signUpData.parentCtn}
                initialNationality={signUpData.parentNationality}
                isParentMode
                enableNationality
                infoI18nKey="signUp.step3.infoParent"
                onValuesChange={persist}
                onNext={handleNext}
            />
        );
    }

    return (
        <IdentityNameTelecomPhoneForm
            initialName={signUpData.name}
            initialTelecom={signUpData.telecom}
            initialPhone={signUpData.ctn}
            initialNationality={signUpData.nationality}
            // SNS 가 제공한 name 은 prefill 만 하고 잠그지 않는다 — 카카오계정 이름은 실명 보장이 없어
            //   (영문명·별명 등) 다날 실명 대조에 실패할 수 있고, 잠그면 사용자가 고칠 수 없다.
            enableNationality
            onValuesChange={persist}
            onNext={handleNext}
        />
    );
};
